<!-- eslint-disable vue/no-v-html -->
<template>
  <v-form
    ref="form"
    v-model="formValid"
    :class="`action-form ${!showPanelTabs && 'pt-16'}`"
    :style="formStyle"
    lazy-validation
    @submit.prevent="
      $refs.form.validate() &&
        $store.dispatch('actionForm/handleFormSubmit', {
          executeRecaptcha: $refs.recaptcha && $refs.recaptcha.execute
        })
    "
  >
    <template v-if="formConfig.recaptcha_enabled">
      <vue-recaptcha
        ref="recaptcha"
        size="invisible"
        :sitekey="recaptchaSiteKey"
        :load-recaptcha-script="true"
        @verify="handleRecaptchaVerify"
        @expired="handleRecaptchaExpired"
      />
    </template>

    <v-tabs v-if="showPanelTabs" class="panel-tabs mb-5" :value="panelTabIndex" fixed-tabs>
      <v-tabs-slider />

      <v-tab
        v-for="(panelTab, index) in panelTabs"
        :key="index"
        :disabled="!panelTabEnabled(panelTab)"
        @click="$store.commit('actionForm/SET_FORM_STATE', panelTab)"
      >
        {{ $t(`action.ltt.panelTabs.${panelTab}`, locale) }}
      </v-tab>
    </v-tabs>

    <transition-group name="fade" tag="div" class="form-inner d-flex flex-column">
      <div v-if="showSpinnerOverlay" key="spinner-overlay" class="spinner-overlay">
        <AllOutSpinner />
      </div>
      <panel-initial
        v-if="formState === FORM_STATES.INITIAL"
        :key="FORM_STATES.INITIAL"
        :on-user-incorrect-click="onUserIncorrectClick"
        :form-valid="formValid"
        @sms-consent-set="onSmsConsentSet"
      />
      <panel-thanks v-if="formState === FORM_STATES.THANKS" :key="FORM_STATES.THANKS" />
      <panel-deactivated
        v-if="formState === FORM_STATES.DEACTIVATED"
        :key="FORM_STATES.DEACTIVATED"
      />
      <panel-ltt-choose-rep
        v-if="formState === FORM_STATES.LTT_CHOOSE_REP"
        :key="FORM_STATES.LTT_CHOOSE_REP"
      />
      <panel-ltt-email v-if="formState === FORM_STATES.LTT_EMAIL" :key="FORM_STATES.LTT_EMAIL" />
      <panel-fundraiser-initial
        v-if="formState === FORM_STATES.FUNDRAISER_INITIAL"
        :key="FORM_STATES.FUNDRAISER_INITIAL"
        :form-style="fundraiserInitialFormStyle"
      />
      <panel-fundraiser-personal
        v-if="formState === FORM_STATES.FUNDRAISER_PERSONAL"
        :key="FORM_STATES.FUNDRAISER_PERSONAL"
      />
      <panel-fundraiser-payment
        v-if="formState === FORM_STATES.FUNDRAISER_PAYMENT"
        :key="FORM_STATES.FUNDRAISER_PAYMENT"
      />
      <panel-fundraiser-thanks
        v-if="formState === FORM_STATES.FUNDRAISER_THANKS"
        :key="FORM_STATES.FUNDRAISER_THANKS"
      />
    </transition-group>
  </v-form>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { mapState, mapGetters } from 'vuex'
import { EVENT_CATEGORIES, EVENT_ACTIONS } from '../utils/tracking'

import AllOutSpinner from '../../ui/AllOutSpinner'

import { FORM_STATES } from '../../store'
import PanelInitial from './panels/PanelInitial'
import PanelThanks from './panels/PanelThanks'
import PanelDeactivated from './panels/PanelDeactivated'
import PanelLttChooseRep from './panels/PanelLttChooseRep'
import PanelLttEmail from './panels/PanelLttEmail'
import PanelFundraiserInitial from './panels/PanelFundraiserInitial'
import PanelFundraiserPersonal from './panels/PanelFundraiserPersonal'
import PanelFundraiserPayment from './panels/PanelFundraiserPayment'
import PanelFundraiserThanks from './panels/PanelFundraiserThanks'

export default {
  name: 'FormInner',
  components: {
    VueRecaptcha,
    AllOutSpinner,
    PanelInitial,
    PanelThanks,
    PanelDeactivated,
    PanelLttChooseRep,
    PanelLttEmail,
    PanelFundraiserInitial,
    PanelFundraiserPersonal,
    PanelFundraiserPayment,
    PanelFundraiserThanks
  },
  props: {
    formStyle: {
      type: Object,
      required: false,
      default: () => ({})
    },
    fundraiserInitialFormStyle: {
      type: Object,
      required: false,
      default: () => ({})
    }
  },
  data: () => ({
    FORM_STATES,
    EVENT_CATEGORIES,
    formValid: false,
    recaptchaSiteKey: '6LetQiEUAAAAAC5mkK_YsHGJjLE7vxjMTIaNn3MA'
  }),
  computed: {
    ...mapState('actionForm', [
      'formState',
      'formConfig',
      'actionId',
      'locale',
      'has3pEmailConsent'
    ]),
    ...mapGetters('actionForm', [
      'panelTabs',
      'showPanelTabs',
      'panelTabEnabled',
      'panelTabIndex',
      'showSpinnerOverlay',
      'isFundraiser'
    ])
  },
  watch: {
    tab(value) {
      this.$store.commit('actionForm/SET_FORM_STATE', value)
    },
    formState(value) {
      this.tab = value
    }
  },
  methods: {
    onSmsConsentSet() {
      this.$refs.form.validate()
    },
    onUserIncorrectClick() {
      this.$store.dispatch('actionForm/setDetectedUserIncorrect')
      // stripping akid so old link won’t re-trigger
      const clean = { ...this.$store.state.actionForm.urlQuery }
      delete clean.akid
      this.$store.commit('actionForm/SET_URL_QUERY', clean)
      // locking the height to avoid layout shift
      this.$parent.$el.style.height = this.$el.offsetHeight
    },
    handleRecaptchaVerify(verifyCode) {
      this.$store.commit('actionForm/UPDATE_POST_DATA', {
        'g-recaptcha-response': verifyCode
      })
      this.$store.dispatch('actionForm/postToAK')
    },
    handleRecaptchaExpired() {
      // eslint-disable-next-line no-console
      console.error('Recaptcha expired')
    },
    resetRecaptcha() {
      this.$refs.recaptcha.reset() // Direct call reset method
    }
  },
  head() {
    const script = []
    if (this.isFundraiser) {
      script.push({
        hid: 'stripe',
        src: 'https://js.stripe.com/v3/',
        callback: () => this.$store.commit('actionForm/UPDATE_FUNDRAISER_UI', { stripeReady: true })
      })
    }
    return { script }
  }
}
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import '../assets/scss/_panels-common.scss';

.form-inner {
  height: 100%;
}

.fade-enter-active {
  transition: opacity 1s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active below version 2.1.8 */ {
  opacity: 0;
}

.action-form {
  display: flex;
  -ms-flex-direction: column;
  flex-direction: column;
  position: relative;

  min-height: 300px;
  max-width: 550px;
  flex: 1 1 auto;

  border-radius: 4px;
  box-shadow: 0 2px 10px 0 rgba(0, 0, 0, 0.3);
  background-color: white;
  font-size: 16px;

  .spinner-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;

    background-color: rgba(250, 250, 250, 0.95);
    z-index: 1;

    .spinner {
      height: 100%;
    }
  }
}

.panel-tabs {
  border-top-left-radius: 4px;
  border-top-right-radius: 4px;
}
</style>
